import { DEFAULT_PAGE_SIZE } from '@shared/constants'
import { useDebounce } from '@shared/hooks'
import { QueryOptions, useInfiniteQuery } from '@tanstack/react-query'
import { Button, Select, SelectProps, Space, Spin } from 'antd'
import { DefaultOptionType } from 'antd/es/select'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type PaginatedResponse<T> = {
  results: T[]
  count: number
}

type ApiCallParams = {
  search: string
  pageParam: number
  pageSize: number
}

type Props<T> = {
  apiCall: (params: ApiCallParams) => Promise<PaginatedResponse<T>>
  getOption: (item: T) => DefaultOptionType
  queryKey: QueryOptions['queryKey']
} & SelectProps

export function InfiniteSelect<T>({
  getOption,
  apiCall,
  queryKey = [],
  ...props
}: Props<T>) {
  const { t } = useTranslation()
  const [trigger, setTrigger] = useState<HTMLElement | null>(null)
  const [search, setSearch] = useState('')

  const debouncedSearch = useDebounce(search)

  const query = useInfiniteQuery({
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await apiCall({
        search: debouncedSearch,
        pageParam,
        pageSize: DEFAULT_PAGE_SIZE
      })

      return {
        results: response?.results,
        total_pages: Math.ceil(response?.count / DEFAULT_PAGE_SIZE),
        current_page: pageParam
      }
    },
    queryKey: [...queryKey, debouncedSearch],
    getNextPageParam: (lastPage: {
      results: T[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = useMemo(() => {
    const result: Required<SelectProps>['options'] =
      query.data?.pages?.flatMap((page) => page.results?.map(getOption)) || []

    if (query.hasNextPage)
      result.push({
        value: 0,
        label: query.isFetchingNextPage ? (
          <Space align="center">
            <Spin spinning />
            {t('pending')}
          </Space>
        ) : (
          <Button
            size="small"
            ref={(e) => setTrigger(e)}
            onClick={() => query.fetchNextPage()}
          >
            {t('load_more')}
          </Button>
        ),
        disabled: true
      })
    return result
  }, [getOption, query, t])

  useEffect(() => {
    if (trigger) query.fetchNextPage()
  }, [trigger])

  return (
    <Select
      placeholder={t('select')}
      loading={query.isLoading}
      options={options}
      onSearch={setSearch}
      onClear={() => setSearch('')}
      showSearch
      filterOption={false}
      allowClear
      {...props}
    />
  )
}
